<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import { APP_EVENTS, onAppEvent, emitAppEvent } from '@/Utils/appEventBus'
import ModalBox from '@/components/modals/ModalBox.vue'
import { useLegacyModalBridge } from '@/composables/useLegacyModalBridge'
import { computed, ref, useTemplateRef, getCurrentInstance, onMounted, onBeforeUnmount, onUnmounted, watch } from 'vue'
import { useNotification } from "@kyvg/vue3-notification"
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import _ from 'lodash'
import Spinner from "@/components/ui/Spinner.vue"
import AlertModal from '@/components/modals/AlertModal.vue'
import Alert from '@/components/Atoms/Alert.vue'
import VODListModal from '@/components/modals/VODListModal.vue'
import AddRecordedVideosModal from '@/components/modals/AddRecordedVideosModal.vue'
import utils from '@/utils'
import StreamService from '@/services/StreamService'
import StreamPlayer from '@/components/ui/StreamPlayer.vue'
import { derivePreviewHost } from '@/Utils/previewHost'
import { selectScheduledPreviewState, countPlayableVideos, playableRuntimeMs, isPlaylistLockedFor } from '@/Utils/scheduledPreviewState'
import { useNow } from '@/composables/useNow'
import VideosUploaderCompactWidget from '@/components/ui/VideosUploaderCompactWidget.vue'
import InlineVideoPreview from '@/components/ui/InlineVideoPreview.vue'
import UppyUploader from '@/components/UppyUploader'
import CountDown from '@/components/ui/CountDown.vue'
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
import DvrArchiveVideoPreviewModal from '@/components/modals/DvrArchiveVideoPreviewModal.vue'
import FormButton from '../../components/Atoms/FormButton.vue'
import FormSwitch from '../../components/Atoms/FormSwitch.vue'
import Tooltip from '../../components/Atoms/Tooltip.vue'
import DropDownMenu from '../../components/Atoms/DropDownMenu.vue'
import RemoveMenuItem from '../../components/ui/RemoveMenuItem.vue'
import IconBase from '../../components/icon/IconBase.vue'
import IconAlertTriangleFill from '../../components/icon/IconAlertTriangleFill.vue'
import IconChevronDown from '../../components/icon/IconChevronDown.vue'
import IconHelp from '../../components/icon/IconHelp.vue'
import IconUpload from '../../components/icon/IconUpload.vue'
import IconPlus from '../../components/icon/IconPlus.vue'
import IconHandleVertical from '../../components/icon/IconHandleVertical.vue'
import IconEye from '../../components/icon/IconEye.vue'
import IconBin from '../../components/icon/IconBin.vue'
import IconPlay from '../../components/icon/IconPlay.vue'
import draggable from 'vuedraggable'
import Badge from '../../components/Badge.vue'

const { isVisible, hide: hideLegacyModal } = useLegacyModalBridge('cloud-import-modal')
const instance = getCurrentInstance()
const root = instance.proxy.$root
const store = useStore()
const { notify } = useNotification()

const props = defineProps({
	stream: {
		type: Object,
		required: true,
		validator(v) {
			return Object.prototype.hasOwnProperty.call(v, '_id')
		}
	},
	mediaPulse: {
		type: Object,
		default() { return {} }
	},
	streamAlive: {
		type: Boolean
	},
	schedulerSettings: {
		type: Object,
		default() { return {} }
	},
	scheduledStorageLimit: {
		type: Object,
		default() { return {} }
	},
	toggleProcessing: {
		type: Boolean
	},
	layout: {
		type: String,
		default: 'sidebar'
	}
})

const emit = defineEmits({
	'video-files': () => true,
	'preview-state': () => true,
})

const addRecordedVideosModal = ref(null)
const showAddRecordedVideos = ref(false)

//TemplateRefs

// Refs

const uppyUploaderActive = ref(true)
const rawUploaderActive = ref(true)
const activeTab = ref('queue')
const notAllowedscheduled = ref(false)
const videoFiles = ref([])
const movePlaylist = ref(false)
const isAnyUpload = ref(false)
const maxFileSizeErrorMessage = ref(null)
const totalFilesPerScheduledStream = ref(1)
const onlineStatus = ref('')
const mediaPulseTick = ref(false)
const playlistLoaded = ref(false)
const currentPage = ref(1)
const itemsPerPage = ref(10)
const focusedVideo = ref({})
const selectedVideo = ref(null)

// Playlist data-loading flag (drives the skeleton while videos are fetched)
const loadingStatus = computed(() => store.getters['Ui/loadingStatus'])

// Computed

const playerHeight = computed(() => props.layout === 'main' ? 'aspect-video w-full' : 'aspect-video w-full')

const baseSubscription = computed(() => store.getters['User/baseSubscription'])
const addonSubscriptions = computed(() => store.getters['User/addonSubscriptions'])
const userEmail = computed(() => store.getters['User/userEmail'])
const userLoaded = computed(() => store.getters['User/userLoaded'])
const activeTeam = computed(() => store.getters['User/activeTeam'])
const userPermissions = computed(() => store.getters['User/userPermissions'])

const isOnline = computed(() => {
	onlineStatus.value = props.mediaPulse
})

const isPrevilegedSub = computed(() => {
	const isPrevileged = _.get(baseSubscription.value, ['package', 'baseCharge'], 0) > 2500

	// Calculate max video files of the scheduled stream
	const activeScheduledSubs = _.filter(addonSubscriptions.value, (sub) => sub.category === 'scheduled' && sub.enabled === true)

	let schedulerMaxNumberOfVideoFile
	activeScheduledSubs.forEach(sub => {
		try {
			if (sub.package.definition.schedulerMaxNumberOfVideoFile) {
				totalFilesPerScheduledStream.value = parseInt(sub.package.definition.schedulerMaxNumberOfVideoFile, 10)
				schedulerMaxNumberOfVideoFile = parseInt(sub.package.definition.schedulerMaxNumberOfVideoFile, 10)
			}
			if (sub.definitionOverride && sub.definitionOverride.schedulerMaxNumberOfVideoFile) {
				totalFilesPerScheduledStream.value = parseInt(sub.definitionOverride.schedulerMaxNumberOfVideoFile, 10)
				schedulerMaxNumberOfVideoFile = parseInt(sub.definitionOverride.schedulerMaxNumberOfVideoFile, 10)
			}
		} catch (error) {
			console.error(error)
		}
	})


	// Set the value to 999 if those setting haven't been set yet
	if (!schedulerMaxNumberOfVideoFile) {
		totalFilesPerScheduledStream.value = 1_000_000;
	}
	return isPrevileged || totalFilesPerScheduledStream.value >= 1
})

const nowTick = useNow(1000)

// `mediaPulseTick` is cleared when the schedule changes, so a pulse left over
// from the previous run can't keep the pane "live" until a fresh one arrives.
const pulseAlive = computed(() => props.streamAlive && mediaPulseTick.value)
// Last live pulse seen for the current schedule; cleared with it.
const lastAliveAt = ref(null)

// The edge serving the scheduler's output comes from the pulse (`hostId`). Until
// it's known there is nothing to play, so the pane keeps its spinner instead of
// mounting a player pointed at a placeholder host.
const previewHost = computed(() => derivePreviewHost(props.mediaPulse, props.stream))
const previewUrl = computed(() => previewHost.value ? `wss://${previewHost.value}/${props.stream.key}/mse_ld?tracks=v1a1` : '')

const playableCount = computed(() => countPlayableVideos(videoFiles.value))

const previewState = computed(() => selectScheduledPreviewState({
	enabled: !!props.stream.enabled,
	alive: pulseAlive.value,
	hostReady: !!previewUrl.value,
	playlistLoaded: playlistLoaded.value,
	playableCount: playableCount.value,
	mode: props.schedulerSettings?.mode ?? null,
	datetime: props.schedulerSettings?.datetime ?? null,
	runtimeMs: playableRuntimeMs(videoFiles.value),
	lastAliveAt: lastAliveAt.value,
	now: nowTick.value,
}))

const videoFilesCount = computed(() => {
	return videoFiles.value.length
})

const sortedVideoFiles = computed(() => {
	const sortedVids = _.sortBy(videoFiles.value, vid => vid.precedence)
	return sortedVids
})

const totalPages = computed(() => {
	return Math.max(1, Math.ceil(sortedVideoFiles.value.length / itemsPerPage.value))
})

const paginatedVideoFiles = computed(() => {
	const start = (currentPage.value - 1) * itemsPerPage.value;
	const end = start + itemsPerPage.value;
	return sortedVideoFiles.value.slice(start, end);
})

const totalDuration = computed(() => {
	let totalSecs = 0;
	videoFiles.value.forEach(v => {
		if (v.enabled && v.mediaInfo && v.mediaInfo.duration) {
			totalSecs += v.mediaInfo.duration;
		}
	});
	if (totalSecs === 0) return '0';
	const hours = Math.floor(totalSecs / 3600);
	const mins = Math.floor((totalSecs % 3600) / 60);
	const secs = Math.floor(totalSecs % 60);
	if (hours > 0) return `${hours}h ${mins}m ${secs}s`;
	return `${mins}m ${secs}s`;
})

const formatDuration = (totalSecs) => {
	if (!totalSecs && totalSecs !== 0) return '--:--';
	const hours = Math.floor(totalSecs / 3600);
	const mins = Math.floor((totalSecs % 3600) / 60);
	const secs = Math.floor(totalSecs % 60);
	if (hours > 0) return `${hours}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
	return `${mins}:${String(secs).padStart(2, '0')}`;
}

const schedulerConfig = computed(() => store.getters['StreamCache/getSchedulerConfigByStreamId'](props.stream._id));

const isLoopMode = computed(() => {
	const propMode = props.schedulerSettings && props.schedulerSettings.mode;
	return propMode === 'loop';
});

const enabledTotalSeconds = computed(() => {
	let total = 0;
	videoFiles.value.forEach(v => {
		if (v.enabled && v.mediaInfo && v.mediaInfo.duration) total += v.mediaInfo.duration;
	});
	return total;
});

// The playlist is on air (dims its rows).
const isLive = computed(() => ['live', 'connecting', 'reconnecting'].includes(previewState.value));

// While the stream is up or about to start, lock the playlist: reordering,
// enable/disable toggles, deletes and uploads are all disabled. Preview stays
// available so users can still inspect the videos that are on air.
const isPlaylistLocked = computed(() =>
	isPlaylistLockedFor(previewState.value, props.schedulerSettings?.datetime ?? null, nowTick.value));

const estimatedEndTime = computed(() => {
	let totalSecs = 0;
	videoFiles.value.forEach(v => {
		if (v.enabled && v.mediaInfo && v.mediaInfo.duration) {
			totalSecs += v.mediaInfo.duration;
		}
	});
	if (totalSecs === 0) return '0';
	const saved = props.schedulerSettings;
	const startMs = (saved && saved.mode === 'datetime' && saved.datetime) ? saved.datetime : Date.now();
	const endDate = new Date(startMs + totalSecs * 1000);
	const hours = endDate.getHours();
	const mins = endDate.getMinutes();
	const ampm = hours >= 12 ? 'PM' : 'AM';
	const displayHours = hours % 12 || 12;
	return `${displayHours}:${mins.toString().padStart(2, '0')} ${ampm}`;
})

const paginationPages = computed(() => {
	const total = totalPages.value;
	const current = currentPage.value;
	const pages = [];
	if (total <= 5) {
		for (let i = 1; i <= total; i++) pages.push(i);
	} else {
		pages.push(1);
		if (current > 3) pages.push('...');
		const start = Math.max(2, current - 1);
		const end = Math.min(total - 1, current + 1);
		for (let i = start; i <= end; i++) pages.push(i);
		if (current < total - 2) pages.push('...');
		pages.push(total);
	}
	return pages;
})

const isOneMinuteBefore = computed(() => previewState.value === 'countdown' && isPlaylistLocked.value)

const maxVideoFileSize = computed(() => {
	let maxSize = null
	const activeScheduledSubs = _.filter(addonSubscriptions.value, (sub) => sub.category === 'scheduled' && sub.enabled === true)

	activeScheduledSubs.forEach(sub => {
		try {
			if (sub.definitionOverride && sub.definitionOverride.schedulerMaxVideoSize) {
				sub.package.definition.schedulerMaxVideoSize = parseFloat(sub.definitionOverride.schedulerMaxVideoSize)
			}
			if (maxSize) {
				const tmpMaxSize = parseFloat(sub.package.definition.schedulerMaxVideoSize)
				if (tmpMaxSize > maxSize) {
					maxSize = tmpMaxSize
				}
			} else {
				maxSize = parseFloat(sub.package.definition.schedulerMaxVideoSize)
			}
		} catch (error) {
			console.error(error)
		}
	})

	if (props.scheduledStorageLimit?.unitsLeft > 0 && props.scheduledStorageLimit?.unitsLeft < parseInt(maxSize * (1000 ** 3))) {
		maxSize = parseFloat(props.scheduledStorageLimit.unitsLeft / (1000 ** 3)).toFixed(1)
	}
	if (props.scheduledStorageLimit?.unitsLeft <= 0) {
		maxSize = 0
	}

	return maxSize
})

const memberPermission = computed(() => {
	return activeTeam.value && userPermissions.value
})

const cannotToggleStream = computed(() => {
	if (!activeTeam.value) return false
	const permission = memberPermission.value && !memberPermission.value.preRecordedStreamToggle
	return permission
})

// Watchers

watch(videoFiles, (filesNew, filesOld) => {
	if (filesNew !== filesOld) {
		emit('video-files', videoFiles.value)
		const noOrderFile = videoFiles.value.find((item) => !item.precedence)
		if (noOrderFile) {
			updateFirstInitOrder()
		}
	}
})

watch(previewState, (state) => emit('preview-state', state), { immediate: true })

watch(() => props.schedulerSettings, () => {
	mediaPulseTick.value = false
	lastAliveAt.value = null
})

watch(() => props.mediaPulse, () => {
	if (!props.mediaPulse) return
	mediaPulseTick.value = true
	if (props.mediaPulse.alive && props.stream.enabled) lastAliveAt.value = Date.now()
}, { immediate: true, deep: true })

// Hooks

onMounted(async () => {
	store.dispatch('Ui/setLoadingStatus', true)
	gatherPlaylistInfo()
	const noOrderFile = videoFiles.value.find((item) => !item.precedence)
	if (noOrderFile) {
		updateFirstInitOrder()
	}
})


// Methods

const onFileSizeLimit = (errorMessage) => {
	maxFileSizeErrorMessage.value = errorMessage
	showModal('alert-video-max-size-exceeded')
}

const uploadEnqueue = (uploadData) => {
	if (isPrevilegedSub.value === false && uploadData.handler === 'raw') {
		uppyUploaderActive.value = false
	}
	if (isPrevilegedSub.value === false && uploadData.handler === 'uppy') {
		rawUploaderActive.value = false
	}
}

const uploadDequeue = (uploadData) => {
	if (isPrevilegedSub.value === false && uploadData.handler === 'raw') {
		uppyUploaderActive.value = true
	}
	if (isPrevilegedSub.value === false && uploadData.handler === 'uppy') {
		rawUploaderActive.value = true
	}
}

const updateFirstInitOrder = async () => {
	const list = []
	_.each(videoFiles.value, (video, index) => {
		const item = _.assign({}, video, {
			precedence: index + 1
		})
		list.push(item)
	})
	videoFiles.value = list
	await StreamService.updateOrderPlaylistVideo(props.stream._id, list)
}

const computeVideoPrecedence = (video) => {
	let precedence = 0
	let siblingVids = videoFiles.value
	if (video) {
		siblingVids = _.filter(videoFiles.value, v => v.id !== video.id)
	}
	precedence = _.size(siblingVids) + 1

	return precedence
}

const moveVideo = async (method = 'up', video) => {
	if (!video || video.file) return

	// video.statusProcessing = true
	const vindex = _.findIndex(videoFiles.value, v => v.id === video.id)
	const vindexSorted = _.findIndex(sortedVideoFiles.value, v => v.id === video.id)
	movePlaylist.value = true

	let video1 = {}
	let video2 = {}

	if (method === 'up') {
		video1 = sortedVideoFiles.value[vindexSorted]
		video2 = sortedVideoFiles.value[vindexSorted - 1]
	}
	if (method === 'down') {
		video1 = sortedVideoFiles.value[vindexSorted]
		video2 = sortedVideoFiles.value[vindexSorted + 1]
	}

	try {
		const video2Index = _.findIndex(videoFiles.value, v => v.id === video2.id)
		const video2Sorted = _.findIndex(sortedVideoFiles.value, v => v.id === video2.id)

		videoFiles.value = utils.updateArrayItem(videoFiles.value, { ...video1, precedence: video2Sorted + 1 }, vindex)
		videoFiles.value = utils.updateArrayItem(videoFiles.value, { ...video2, precedence: vindexSorted + 1 }, video2Index)

		await StreamService.updateOrderPlaylistVideo(props.stream._id, sortedVideoFiles.value)
		movePlaylist.value = false
	} catch (e) {
		console.log('e', e)
	}
}

const toggleVideoStatus = async (video) => {
	const vindex = _.findIndex(videoFiles.value, v => v.id === video.id)
	videoFiles.value = utils.updateArrayItem(videoFiles.value, { ...video, statusProcessing: true }, vindex)
	const nstate = video.enabled
	try {
		await StreamService.togleStreamPlaylistVideoStatus(
			props.stream._id,
			video.id,
			nstate
		)
		video.enabled = nstate
	} catch (e) {
		console.log('e', e)
	}
	videoFiles.value = utils.updateArrayItem(videoFiles.value, { ...video, statusProcessing: false }, vindex)
}

const switchOptionsTab = (tabName) => {
	activeTab.value = tabName
}

const onScheduleCountdownDone = () => {
	nowTick.value = Date.now()
}

const onVideoAdded = (videoItem) => {
	videoItem = preparePlaylistVideoItem(videoItem)
	// Reassign rather than push: the parent (setup bar, Stream control) only
	// hears about the playlist when the array itself changes.
	videoFiles.value = [...videoFiles.value, videoItem]
}

const onVideoItemUpdate = (videoId, updates) => {
	const vindex = _.findIndex(videoFiles.value, { id: videoId })
	if (vindex > -1) {
		const videoItem = _.assign({}, videoFiles.value[vindex], updates)
		videoFiles.value = utils.updateArrayItem(
			videoFiles.value,
			videoItem,
			vindex
		)
	}
}

const onVideoRemoved = (videoId) => {
	const vindex = _.findIndex(videoFiles.value, { id: videoId })
	videoFiles.value = utils.removeArrayItem(videoFiles.value, vindex)

	if (!videoFilesCount.value) {
		switchOptionsTab('queue')
	}
}

const onVideoQueueEmptied = () => {
	switchOptionsTab('playlist')
}

const gatherPlaylistInfo = async () => {
	let videoList = []
	try {
		videoList = await StreamService.getStreamPlaylist(props.stream._id)
	} catch (e) {
		notify({
			group: 'error',
			text: 'Couldn’t retrieve the playlist videos.'
		})
	}

	videoList = _.compact(videoList).map(file => preparePlaylistVideoItem(file))
	if (videoList.length) {
		switchOptionsTab('playlist')
	}

	videoFiles.value = videoList
	playlistLoaded.value = true
	// this.playlistProcessing = false;
	store.dispatch('Ui/setLoadingStatus', false)
}

onBeforeUnmount(onAppEvent(APP_EVENTS.VIDEO_UPLOADED, gatherPlaylistInfo))

const preparePlaylistVideoItem = (videoFile) => {
	videoFile.removing = false
	videoFile.statusProcessing = false
	videoFile.uploadTime = new Date(videoFile.uploadTime)
	return videoFile
}

const showStreamToggleModal = (ev) => {
	ev.preventDefault()
	ev.stopPropagation()
	if (localStorage.getItem('notAllowedscheduled')) {
		notAllowedscheduled.value = (localStorage.getItem('notAllowedscheduled') == 'true')
	}
	if (notAllowedscheduled.value && !props.stream.enabled) {
		showModal('increase-limit')
	}
	if (cannotToggleStream.value) return
	// if (this.stream.enabled){
	if ((!notAllowedscheduled.value || (notAllowedscheduled.value && props.stream.enabled))) {
		if (cannotToggleStream.value) return
		showModal('modalToggleStream' + props.stream._id, '#btnShow')
	}
}

const goToPage = (page) => {
	if (page === '...' || page < 1 || page > totalPages.value) return;
	expandedPreviewId.value = null;
	currentPage.value = page;
}

const openFilePicker = () => {
	emitAppEvent(APP_EVENTS.OPEN_VOD_UPLOADER, { stream: props.stream })
}

const onPageReorder = (reorderedSlice) => {
	const start = (currentPage.value - 1) * itemsPerPage.value;
	const fullSorted = [...sortedVideoFiles.value];
	fullSorted.splice(start, reorderedSlice.length, ...reorderedSlice);
	videoFiles.value = fullSorted.map((video, index) => ({
		...video,
		precedence: index + 1
	}));
}

const onDragEnd = async () => {
	expandedPreviewId.value = null;
	await StreamService.updateOrderPlaylistVideo(props.stream._id, videoFiles.value);
}

const openAddRecordedVideos = () => {
	showModal('modal-add-recorded-videos');
}

const openVODListModal = () => {
	showModal('vod-import-modal');
}

const requestVideoRemoval = (videoItem) => {
	focusedVideo.value = videoItem;
	showModal('confirm-video-removal');
}

const requestVideoPreview = async (media) => {
	selectedVideo.value = media;
	setTimeout(() => {
		showModal('modal-dvr-archive-video-preview');
	}, 100);
}

// Inline preview: expand one row at a time (accordion) instead of a popup
const expandedPreviewId = ref(null)
const previewKey = (media) => media.id || media.name
const isPreviewExpanded = (media) => expandedPreviewId.value === previewKey(media)
const togglePreview = (media) => {
	expandedPreviewId.value = isPreviewExpanded(media) ? null : previewKey(media)
}

const enabledCount = computed(() => videoFiles.value.filter(v => v.enabled).length)

const isVideoRemovalDisabled = (media) => media.statusProcessing || isPlaylistLocked.value

const deleteMenus = {}
const setDeleteMenuRef = (media, el) => {
	const k = previewKey(media)
	if (el) deleteMenus[k] = el
	else delete deleteMenus[k]
}
const closeDeleteMenu = (media) => {
	deleteMenus[previewKey(media)]?.hide?.()
}

const onInlineVideoRemove = (media) => {
	focusedVideo.value = media
	closeDeleteMenu(media)
	removeVideoFile()
}

// Open the delete confirm upward for the last rows so it doesn't overflow the card.
const deleteMenuPosition = (index) => (index >= paginatedVideoFiles.value.length - 2 ? 'top-right' : 'right')

const removeVideoFile = async () => {
	const videoId = focusedVideo.value.id;
	const vindex = _.findIndex(videoFiles.value, { id: videoId });
	onVideoItemUpdate(videoId, { removing: true });
	await StreamService.removeStreamPlaylistVideo(
		props.stream._id,
		videoId,
		focusedVideo.value.type === 'live-to-vod'
	);
	onVideoRemoved(videoId);
}
</script>

<template>
	<div>
		<div class="content-container bg-surface-2 rounded-xl border border-surface-3 overflow-hidden">
			<div :class="[playerHeight, 'bg-black flex flex-col gap-y-2 items-center justify-center overflow-hidden']"
				:data-preview-state="previewState">
				<div v-if="previewState === 'disabled'"
					class="w-full h-full flex flex-col gap-y-2 items-center justify-center !p-4">
					<p class="text-center text-lg text-surface-7 font-medium m-0">
						Stream disabled
					</p>
					<small class="text-sm text-surface-5">Enable it to start streaming.</small>
					<!-- The notice is about one missing permission, so it answers for
					     that one: bound to `memberPermission` it greeted every team
					     member, including the ones the button works for. -->
					<Tooltip :active="cannotToggleStream">
						<FormButton :disabled="toggleProcessing || cannotToggleStream" @click="showStreamToggleModal">
							Enable Stream
							<template #content>
								You don’t have permission for this action. Contact the team owner at <a class="text-surface-8 text-sm"
									:href="'mailto:' + userEmail">{{ userEmail }}</a> to request access.
							</template>
						</FormButton>
					</Tooltip>
				</div>
				<!-- Keyed by host: if the scheduler restarts on another edge the player remounts there. -->
				<stream-player v-else-if="previewState === 'live'" :key="mediaPulse.hostId" :stream="stream"
					:stream-url="previewUrl" :media-pulse="mediaPulse"
					class="w-full h-full flex flex-col gap-y-2 items-center justify-center relative" />

				<div v-else-if="previewState === 'needs-videos'"
					class="w-full h-full flex flex-col gap-y-1.5 items-center justify-center !p-4 text-center">
					<template v-if="videoFilesCount === 0">
						<p class="text-surface-8 font-medium m-0">Add a video to get started</p>
						<p class="text-tiny text-surface-6 mb-0">Build your playlist first, then choose how and when it goes live.</p>
					</template>
					<template v-else>
						<p class="text-surface-8 font-medium m-0">Turn on a video to go live</p>
						<p class="text-tiny text-surface-6 mb-0">Every video in the playlist is switched off.</p>
					</template>
				</div>

				<div v-else-if="previewState === 'countdown'"
					class="w-full h-full flex flex-col gap-y-1.5 items-center justify-center text-center !p-4">
					<span class="mb-0.5 inline-flex size-2 rounded-full bg-main animate-pulse"></span>
					<p class="text-surface-8 font-medium m-0">Going live in</p>
					<CountDown :endDate="schedulerSettings.datetime" @done="onScheduleCountdownDone" class="text-sm text-surface-9 font-medium tabular-nums" />
				</div>

				<div v-else-if="previewState === 'starting'"
					class="w-full h-full flex flex-col gap-y-1.5 items-center justify-center !p-4 text-center">
					<span class="mb-0.5 inline-flex size-2 rounded-full bg-main animate-pulse"></span>
					<p class="text-surface-8 font-medium m-0">Starting any moment now</p>
					<p class="text-tiny text-surface-6 mb-0">Your stream is about to begin — the preview will appear shortly.</p>
				</div>

				<div v-else-if="previewState === 'ended' || previewState === 'missed'"
					class="w-full h-full flex flex-col gap-y-1.5 items-center justify-center !p-4 text-center">
					<p class="text-surface-8 font-medium m-0">
						{{ previewState === 'ended' ? 'Scheduled stream has ended' : 'Scheduled time has passed' }}
					</p>
					<p class="text-tiny text-surface-6 mb-0">Reset or schedule again to run it another time.</p>
				</div>

				<div v-else-if="previewState === 'idle'"
					class="w-full h-full flex flex-col gap-y-1.5 items-center justify-center !p-4 text-center">
					<p class="text-surface-8 font-medium m-0">Not scheduled yet</p>
					<p class="text-tiny text-surface-6 mb-0">Choose how your playlist goes live in Stream control.</p>
				</div>

				<!-- loading / connecting / reconnecting: playlist or the pulse's serving host is still on its way -->
				<div v-else class="w-full h-full flex flex-col gap-y-1.5 items-center justify-center !p-4 text-center">
					<Spinner text="" classes="text-surface-7 mb-1" spinner-color="var(--c-surface-7)" spinner-size="18px" />
					<template v-if="previewState === 'reconnecting'">
						<p class="text-surface-8 font-medium m-0">Reconnecting…</p>
						<p class="text-tiny text-surface-6 mb-0">Your stream dropped for a moment.</p>
					</template>
					<p v-else-if="previewState === 'connecting'" class="text-surface-8 font-medium m-0">Connecting to your stream…</p>
					<p v-else class="text-surface-8 font-medium m-0">Loading playlist…</p>
				</div>
			</div>
			<!-- Skeleton: mirrors the playlist layout so nothing shifts when data arrives -->
			<div v-if="loadingStatus" class="border-t border-surface-3" aria-hidden="true">
				<div class="flex items-center justify-between px-4 py-3 border-b border-surface-3">
					<div class="h-4 w-16 rounded bg-surface-3 animate-pulse"></div>
					<div class="flex items-center gap-x-2">
						<div class="h-7 w-28 rounded-md bg-surface-3 animate-pulse"></div>
						<div class="h-7 w-40 rounded-md bg-surface-3 animate-pulse"></div>
					</div>
				</div>
				<div class="p-4">
					<div class="grid grid-cols-3 mb-3 bg-surface-1 border border-surface-3 rounded-lg overflow-hidden">
						<div v-for="n in 3" :key="'sk-stat-' + n" class="px-3 py-2.5"
							:class="{ 'border-r border-surface-3': n < 3 }">
							<div class="h-3 w-20 rounded bg-surface-3 animate-pulse mb-2"></div>
							<div class="h-4 w-12 rounded bg-surface-3 animate-pulse"></div>
						</div>
					</div>
					<div class="flex items-center gap-x-3 px-3 py-2">
						<div class="h-2.5 w-12 rounded bg-surface-3 animate-pulse flex-1 max-w-[40px]"></div>
					</div>
					<div class="flex flex-col gap-y-1">
						<div v-for="n in 3" :key="'sk-row-' + n"
							class="flex items-center gap-x-3 pl-3 pr-2 py-3 bg-surface-1 border border-surface-3 rounded-lg">
							<div class="size-4 rounded bg-surface-3 animate-pulse shrink-0"></div>
							<div class="h-4 rounded bg-surface-3 animate-pulse flex-1 max-w-[220px]"></div>
							<div class="h-4 w-12 rounded bg-surface-3 animate-pulse shrink-0"></div>
							<div class="h-5 w-9 rounded-full bg-surface-3 animate-pulse shrink-0"></div>
							<div class="h-6 w-16 rounded-md bg-surface-3 animate-pulse shrink-0"></div>
						</div>
					</div>
				</div>
			</div>
			<div v-else>
				<!-- Playlist -->
				<div class="border-t border-surface-3">
					<!-- Playlist Header -->
					<div class="playlist-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 w-full px-4 py-3 border-b border-surface-3">
						<span class="hidden sm:inline text-sm font-medium">Playlist</span>
						<div class="flex w-full sm:w-auto flex-row flex-wrap items-center gap-2">
							<videos-uploader-compact-widget class="shrink-0" v-if="rawUploaderActive" :stream="stream" :media-online="isOnline"
								:stream-alive="streamAlive" :subscription="subscription" :multiple-uploads="isPrevilegedSub"
								:is-one-minute-before="isOneMinuteBefore" :video-files="videoFiles" :mp4-only="true"
								:total-files-per-stream="totalFilesPerScheduledStream"
								:is-disabled-control="isPlaylistLocked"
								@video-added="onVideoAdded" @video-queue-emptied="onVideoQueueEmptied" @upload-enqueue="uploadEnqueue"
								@upload-dequeue="uploadDequeue" @updated="gatherPlaylistInfo" @show-add-recorded="openAddRecordedVideos" />
							<FormButton class="!max-w-none !overflow-visible" size="sm"
								:disabled="isPlaylistLocked"
								@click.stop="openAddRecordedVideos">
								<IconBase class="mr-1"><IconPlus /></IconBase>
								Add Recorded Videos
							</FormButton>
						</div>
					</div>
					<!-- Live notice: playlist editing is locked while the stream is on air -->
					<div v-if="isPlaylistLocked" class="px-4 pt-3">
						<Alert type="normal" :text="isLive
							? 'Stream is live — the playlist is locked. Disable the stream to edit or upload videos.'
							: 'Stream is about to start — the playlist is locked until the run is over.'">
						</Alert>
					</div>
					<!-- Full-width target: the uploader widget teleports its "incompatible file" warning here so it spans the body instead of breaking the header -->
					<div id="scheduled-incompatible-alert" class="px-4"></div>
					<!-- Playlist Content -->
					<div class="p-4">
					<!-- Stats Row -->
					<div class="hidden md:grid mb-3 text-sm bg-surface-1 border border-surface-3 rounded-lg" :class="isLoopMode ? 'grid-cols-2' : 'grid-cols-3'">
						<div class="px-3 py-2.5 border-r border-surface-3">
							<span class="text-surface-7 text-xs">Total videos</span>
							<p class="m-0 text-white text-sm font-medium leading-tight">{{ videoFilesCount }}</p>
							<p v-if="enabledCount !== videoFilesCount" class="m-0 mt-0.5 text-tiny text-surface-6 leading-tight">{{ enabledCount }} enabled</p>
						</div>
						<div class="px-3 py-2.5" :class="{ 'border-r border-surface-3': !isLoopMode }">
							<span class="text-surface-7 text-xs">{{ enabledCount !== videoFilesCount ? 'Enabled duration' : 'Total duration' }}</span>
							<p class="m-0 text-white text-sm font-medium">{{ totalDuration }}</p>
						</div>
						<div v-if="!isLoopMode" class="px-3 py-2.5">
							<span class="text-surface-7 text-xs">Estimated end time</span>
							<p class="m-0 text-white text-sm font-medium">{{ estimatedEndTime }}</p>
						</div>
					</div>
					<!-- Stats List (mobile) -->
					<div class="flex flex-col md:hidden mb-3 text-sm border border-surface-3 rounded-md px-3 py-2 gap-y-1">
						<div class="flex justify-between">
							<span class="text-surface-7">Total videos:</span>
							<span class="text-white font-medium">{{ videoFilesCount }}<span v-if="enabledCount !== videoFilesCount" class="text-surface-6 font-normal"> · {{ enabledCount }} enabled</span></span>
						</div>
						<div class="flex justify-between">
							<span class="text-surface-7">{{ enabledCount !== videoFilesCount ? 'Enabled duration' : 'Total duration' }}:</span>
							<span class="text-white font-medium">{{ totalDuration }}</span>
						</div>
						<div v-if="!isLoopMode" class="flex justify-between">
							<span class="text-surface-7">Estimated end time</span>
							<span class="text-white font-medium">{{ estimatedEndTime }}</span>
						</div>
					</div>

					<!-- Empty State -->
					<div v-if="videoFilesCount === 0" class="flex flex-col items-center justify-center py-8 text-center">
						<p class="text-base font-medium text-surface-9 mb-1">Add videos to your playlist</p>
						<p class="text-tiny text-surface-7 mb-4">Upload a video or add a recorded one to start building your stream — {{ videoFilesCount }}/{{ totalFilesPerScheduledStream }} files</p>
						<div class="flex flex-wrap items-center justify-center gap-2">
							<FormButton :disabled="isPlaylistLocked" @click="openFilePicker">
								<IconBase class="mr-1">
									<IconUpload />
								</IconBase>
								Upload video
							</FormButton>
							<FormButton type="secondary" :disabled="isPlaylistLocked" @click="openAddRecordedVideos">
								<IconBase class="mr-1">
									<IconPlus />
								</IconBase>
								Add Recorded Videos
							</FormButton>
						</div>
					</div>

					<!-- Table Header -->
					<div v-if="videoFilesCount > 0" class="hidden md:flex items-center gap-x-3 pl-3 pr-2 py-2 text-tiny uppercase tracking-wider text-surface-6">
						<span class="flex-1 min-w-0">Video</span>
						<span class="w-[72px] text-right shrink-0">Duration</span>
						<span class="w-[60px] text-center shrink-0">Status</span>
						<span class="w-[110px] text-right shrink-0">Actions</span>
					</div>

					<!-- Table Rows -->
					<draggable v-if="videoFilesCount > 0" :model-value="paginatedVideoFiles"
						@update:model-value="onPageReorder" @end="onDragEnd"
						:item-key="(item) => item.id || item.name" handle=".drag-handle"
						:disabled="isPlaylistLocked"
						tag="div" class="flex flex-col gap-y-1">
						<template #item="{ element: media, index }">
							<div>
							<div class="flex flex-wrap md:flex-nowrap items-center gap-x-3 gap-y-2 pl-3 pr-2 py-3 md:py-2 bg-surface-1 border rounded-lg transition-colors"
								:class="isPreviewExpanded(media) ? 'border-surface-4 !rounded-b-none' : 'border-surface-3 hover:border-surface-4'">
								<!-- Video name with drag handle + play order -->
								<div class="flex items-center gap-x-2 min-w-0 w-full md:w-auto md:flex-1">
									<IconBase class="drag-handle text-surface-7 shrink-0"
										:class="isPlaylistLocked ? 'opacity-50 cursor-not-allowed' : 'cursor-grab active:cursor-grabbing'">
										<IconHandleVertical />
									</IconBase>
									<span class="text-tiny text-surface-6 tabular-nums shrink-0 w-4 text-center">{{ (currentPage - 1) * itemsPerPage + index + 1 }}</span>
									<span class="text-sm truncate transition-opacity" :class="isLive ? 'opacity-45' : ''"
										:title="media.fileNameOriginal || media.fileName">{{ media.fileName }}</span>
								</div>
								<!-- Duration -->
								<span class="text-sm text-surface-8 tabular-nums md:w-[72px] md:text-right shrink-0 transition-opacity"
									:class="isLive ? 'opacity-45' : ''">
									<span class="md:hidden text-surface-6">Duration: </span>
									{{ media.mediaInfo && media.mediaInfo.duration ? formatDuration(media.mediaInfo.duration) : '--:--' }}
								</span>
								<!-- Status -->
								<div class="flex items-center justify-center md:w-[60px] shrink-0"
									:title="media.enabled ? 'Enabled — this video plays in the scheduled stream' : 'Disabled — this video is skipped'">
									<FormSwitch v-if="!media.file && !media.removing"
										size="sm"
										:model-value="!!media.enabled"
										:disabled="isPlaylistLocked || media.statusProcessing"
										@update:model-value="(val) => { media.enabled = val; toggleVideoStatus(media) }" />
									<span v-else
										class="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-orange text-white">Pending</span>
								</div>
								<!-- Actions -->
								<div class="flex items-center justify-end gap-x-1 ml-auto md:ml-0 md:w-[110px] shrink-0">
									<FormButton type="secondary" size="xs" @click="togglePreview(media)">
										{{ isPreviewExpanded(media) ? 'Close' : 'Preview' }}
									</FormButton>
									<DropDownMenu :ref="(el) => setDeleteMenuRef(media, el)" isIcon no-border type="secondary" aria-label="Delete video"
										no-popper :position="deleteMenuPosition(index)" :disabled="isVideoRemovalDisabled(media)">
										<template #toggle-button>
											<IconBase class="hover:text-error transition-colors"><IconBin /></IconBase>
										</template>
										<template #menu-items>
											<RemoveMenuItem immediate confirm-text="Delete this video? This can't be undone."
												@confirm="onInlineVideoRemove(media)" @cancel="closeDeleteMenu(media)" />
										</template>
									</DropDownMenu>
								</div>
							</div>
							<!-- Inline preview (accordion) — replaces the popup, keeps you in context -->
							<Transition
								enter-active-class="transition-all duration-300 ease-out overflow-hidden"
								enter-from-class="max-h-0 opacity-0"
								enter-to-class="max-h-[520px] opacity-100"
								leave-active-class="transition-all duration-200 ease-in overflow-hidden"
								leave-from-class="max-h-[520px] opacity-100"
								leave-to-class="max-h-0 opacity-0">
								<div v-if="isPreviewExpanded(media)"
									class="bg-surface-1 border border-t-0 border-surface-4 rounded-b-lg px-3 pt-1 pb-3">
									<InlineVideoPreview :video="media" :stream="stream" />
									<div v-if="media.mediaInfo && (media.mediaInfo.width || media.mediaInfo.duration)"
										class="flex flex-wrap items-center gap-x-5 gap-y-1 mt-2 text-tiny text-surface-6">
										<span v-if="media.mediaInfo.width && media.mediaInfo.height">Resolution
											<span class="text-surface-8 tabular-nums">{{ media.mediaInfo.width }}×{{ media.mediaInfo.height }}</span></span>
										<span v-if="media.mediaInfo.duration">Duration
											<span class="text-surface-8 tabular-nums">{{ formatDuration(media.mediaInfo.duration) }}</span></span>
									</div>
								</div>
							</Transition>
							</div>
						</template>
					</draggable>

					<!-- Pagination -->
					<div v-if="totalPages > 1" class="flex items-center justify-center gap-x-1 mt-4">
						<FormButton type="link" isIcon size="xs" :disabled="currentPage === 1" @click="goToPage(1)">
							&laquo;
						</FormButton>
						<FormButton type="link" isIcon size="xs" :disabled="currentPage === 1" @click="goToPage(currentPage - 1)">
							&lsaquo;
						</FormButton>
						<template v-for="page in paginationPages" :key="'page-' + page">
							<span v-if="page === '...'" class="px-1 text-surface-7 text-sm">...</span>
							<FormButton v-else type="link" size="xs"
								:customClasses="page === currentPage ? 'bg-surface-5 text-white rounded px-2 py-1 text-sm' : 'text-surface-8 px-2 py-1 text-sm hover:text-white'"
								@click="goToPage(page)">
								{{ page }}
							</FormButton>
						</template>
						<FormButton type="link" isIcon size="xs" :disabled="currentPage === totalPages" @click="goToPage(currentPage + 1)">
							&rsaquo;
						</FormButton>
						<FormButton type="link" isIcon size="xs" :disabled="currentPage === totalPages" @click="goToPage(totalPages)">
							&raquo;
						</FormButton>
					</div>

					<!-- Recommended settings -->
					<div class="border-t border-surface-3 my-3"></div>
					<a href="https://docs.castr.com/en/articles/5161326-recommended-settings-for-recorded-videos" target="_blank"
						class="text-surface-7 flex items-center justify-center gap-x-2 text-sm text-center hover:text-white hover:no-underline">
						<IconBase>
							<IconHelp />
						</IconBase>
						<span>Recommended settings</span>
					</a>
					</div>
				</div>

				<!-- stream/preview configurations -->
				<ModalBox v-model="isVisible" size="md" hide-close header-class="!hidden" wrapper-classes="lg:h-auto lg:max-h-none p-0">

					<UppyUploader class="cloud-import" :stream="stream" :video-files="videoFiles"
						:total-files-per-stream="totalFilesPerScheduledStream"
						:maxFileSize="maxVideoFileSize ? (maxVideoFileSize * (1000 ** 3)) : maxVideoFileSize"
						:storageLimit="scheduledStorageLimit" @upload-success="onVideoAdded" @upload-enqueue="uploadEnqueue"
						@upload-dequeue="uploadDequeue" @filesize-restriction-exceeded="onFileSizeLimit" />
				
</ModalBox>
				<VODListModal />
			</div>
		</div>
		<confirm-modal
			:message="'You\'re about to delete the video “' + (focusedVideo && focusedVideo.fileName || '') + '”. This can\'t be undone.'"
			modal-id="confirm-video-removal" modal-type="danger" ok-text="Delete" cancel-text="Keep"
			@modal-confirm="removeVideoFile()" />
		<dvr-archive-video-preview-modal v-if="selectedVideo" :stream="stream" :video="selectedVideo" />
		<alert-modal modal-id="alert-video-encoding"
			message="Only MP4 videos are allowed. Please upload a video in a supported format."
			ok-text="Got it, thanks" />

		<alert-modal modal-id="alert-video-max-size-exceeded"
			:message="maxFileSizeErrorMessage || 'This file is too large. Upload a smaller file or upgrade your plan to continue.'"
			ok-text="Got it, thanks" />
		<AddRecordedVideosModal :stream="stream" :video-files="videoFiles" :total-files-per-stream="totalFilesPerScheduledStream" @updated="gatherPlaylistInfo" />
	</div>
</template>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>

.content-container :deep(.player-container) {
	height: 100%;
}


.content-container :deep(video.container-size) {
	width: 100% !important;
	height: 100% !important;
	object-fit: cover !important;
}
</style>
